import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Grid,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  equipmentStatusValues,
  equipmentConditionValues,
  EquipmentStatus,
  EquipmentCondition,
} from "dora-contracts";
import { useAppDispatch, useSelector } from "../../../redux-store";
import {
  selectInitialFormData,
  selectIsOpen,
} from "../../../ducks/app/equipment-dialog/selectors";
import { FormData, close } from "../../../ducks/app/equipment-dialog";
import * as dataActions from "../../../ducks/data/equipment";
import { loadEquipmentTypes } from "../../../ducks/data/equipment-types";
import { selectEquipmentTypes } from "../../../ducks/data/equipment-types/selectors";
import { loadEquipmentDepots } from "../../../ducks/data/equipment-depots";
import { selectEquipmentDepots } from "../../../ducks/data/equipment-depots/selectors";
import { AddressChange } from "../../NewPlaceSearch";
import PlaceSearchWithSavedLocations from "../../PlaceSearchWithSavedLocations/PlaceSearchWithSavedLocations";
import { UFDoraSelect } from "dora-shared/frontend";
import emptyToNull from "./empty-to-null";

const OpenEquipmentDialog = ({ initialData }: { initialData: FormData }) => {
  const { t } = useTranslation("equipment");
  const dispatch = useAppDispatch();
  const types = useSelector(selectEquipmentTypes);
  const depots = useSelector(selectEquipmentDepots);

  useEffect(() => {
    dispatch(loadEquipmentTypes());
    dispatch(loadEquipmentDepots());
  }, [dispatch]);

  // A numbered type means one physical unit per row: quantity is always 1
  // and the number is what identifies it.
  const numberedTypeIds = useMemo(
    () => new Set(types.filter((x) => x.isNumbered).map((x) => x.id)),
    [types]
  );

  const schema = useMemo(
    () =>
      yup.object({
        equipmentTypeId: yup
          .string()
          .nullable()
          .required(t("units.fields.typeRequired")),
        number: yup
          .string()
          .nullable()
          .test(
            "required-for-numbered",
            t("units.fields.numberRequired"),
            (v, ctx) =>
              !numberedTypeIds.has(ctx.parent.equipmentTypeId) || !!v?.trim()
          ),
        serialNumber: yup.string().nullable(),
        // A numbered type is one unit per row, and the field is disabled and
        // forced to 1, so the count is only asked of bulk rows.
        quantity: yup
          .mixed<number>()
          .test(
            "at-least-one-when-bulk",
            t("units.fields.quantityMin"),
            (v, ctx) =>
              numberedTypeIds.has(ctx.parent.equipmentTypeId) ||
              (typeof v === "number" && Number.isInteger(v) && v >= 1)
          ),
        status: yup.mixed<EquipmentStatus>().required(),
        condition: yup.mixed<EquipmentCondition>().required(),
        ownWeight: yup.string().nullable(),
        depotId: yup.string().nullable(),
        homeLocation: yup.string().nullable(),
        // A depot is the unit's home, so the free base location is only
        // required for units that are not attached to one. The place search
        // fills both coordinates in at once, so each one carries the rule
        // itself rather than one field reporting an error that belongs to the
        // other.
        homeLat: yup
          .number()
          .nullable()
          .when("depotId", {
            is: (depotId: string | null) => depotId == null,
            then: (field) =>
              field.required(t("units.fields.homeLocationRequired")),
          }),
        homeLon: yup
          .number()
          .nullable()
          .when("depotId", {
            is: (depotId: string | null) => depotId == null,
            then: (field) =>
              field.required(t("units.fields.homeLocationRequired")),
          }),
        note: yup.string().nullable(),
      }),
    [t, numberedTypeIds]
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: initialData,
  });
  const homeLocation = watch("homeLocation");
  const homeLat = watch("homeLat");
  const depotId = watch("depotId");
  // Both coordinates carry the same rule and are set together, so whichever of
  // them yup flagged is the one "no base location picked" error.
  const homePositionError = errors.homeLat ?? errors.homeLon;
  const equipmentTypeId = watch("equipmentTypeId");
  const isNumbered = !!equipmentTypeId && numberedTypeIds.has(equipmentTypeId);

  const typeOptions = useMemo(
    () => [
      { value: null, label: t("units.fields.noType") },
      ...types.map((type) => ({ value: type.id, label: type.name })),
    ],
    [types, t]
  );
  const depotOptions = useMemo(
    () => [
      { value: null, label: t("units.fields.noDepot") },
      ...depots.map((depot) => ({ value: depot.id, label: depot.name })),
    ],
    [depots, t]
  );
  const statusOptions = useMemo(
    () =>
      equipmentStatusValues.map((v) => ({
        value: v,
        label: t(`statuses.${v}`),
      })),
    [t]
  );
  const conditionOptions = useMemo(
    () =>
      equipmentConditionValues.map((v) => ({
        value: v,
        label: t(`conditions.${v}`),
      })),
    [t]
  );

  useEffect(() => {
    if (isNumbered) {
      setValue("quantity", 1);
      clearErrors("quantity");
    }
  }, [isNumbered, setValue, clearErrors]);

  // The place search owns the unit's base location: picking a place fills in
  // the name and both coordinates, clearing it empties all three.
  const onPlaceSelected = useCallback(
    (place: AddressChange | null) => {
      if (!place) {
        setValue("homeLocation", null);
        setValue("homeLat", null);
        setValue("homeLon", null);
        return;
      }
      clearErrors(["homeLat", "homeLon"]);
      setValue(
        "homeLocation",
        place.placeName || place.address || place.place || "",
        { shouldValidate: true }
      );
      setValue("homeLat", place.coord.lat, { shouldValidate: true });
      setValue("homeLon", place.coord.lon, { shouldValidate: true });
    },
    [setValue, clearErrors]
  );

  const onSubmit = async (values: FormData) => {
    const payload = {
      equipmentTypeId: values.equipmentTypeId,
      number: emptyToNull(values.number),
      serialNumber: emptyToNull(values.serialNumber),
      quantity: isNumbered ? 1 : Number(values.quantity),
      status: values.status,
      condition: values.condition,
      ownWeight: emptyToNull(values.ownWeight),
      depotId: values.depotId,
      homeLocation: emptyToNull(values.homeLocation),
      homeLat: values.homeLat ?? null,
      homeLon: values.homeLon ?? null,
      note: emptyToNull(values.note),
    };
    if (initialData.id) {
      await dispatch(
        dataActions.updateEquipment({ ...payload, id: initialData.id })
      );
    } else {
      await dispatch(dataActions.addEquipment(payload));
    }
  };

  return (
    <Dialog open onClose={() => dispatch(close())} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {initialData.id ? t("units.editTitle") : t("units.addTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <UFDoraSelect
              name="equipmentTypeId"
              control={control}
              label={t("units.fields.type")}
              options={typeOptions}
              fullWidth
            />
            {errors.equipmentTypeId && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                {errors.equipmentTypeId.message}
              </Typography>
            )}
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label={t("units.fields.number")}
                  required={isNumbered}
                  {...register("number")}
                  error={!!errors.number}
                  helperText={errors.number?.message}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label={t("units.fields.serialNumber")}
                  {...register("serialNumber")}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label={t("units.fields.quantity")}
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  disabled={isNumbered}
                  {...register("quantity", { valueAsNumber: true })}
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  label={t("units.fields.ownWeight")}
                  {...register("ownWeight")}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <UFDoraSelect
                  name="status"
                  control={control}
                  label={t("units.fields.status")}
                  options={statusOptions}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <UFDoraSelect
                  name="condition"
                  control={control}
                  label={t("units.fields.condition")}
                  options={conditionOptions}
                  fullWidth
                />
              </Grid>
            </Grid>
            <div>
              <UFDoraSelect
                name="depotId"
                control={control}
                label={t("units.fields.depot")}
                options={depotOptions}
                nullable
                fullWidth
              />
            </div>
            {!depotId && (
              <div>
                <PlaceSearchWithSavedLocations
                  placeError={!!homePositionError}
                  onPlaceSelected={onPlaceSelected}
                />
                <Typography
                  variant="caption"
                  color={homePositionError ? "error" : "text.secondary"}
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {homeLat != null && homeLocation
                    ? `${t("units.fields.homeLocation")}: ${homeLocation}`
                    : homePositionError?.message ?? ""}
                </Typography>
              </div>
            )}
            <TextField
              label={t("units.fields.note")}
              {...register("note")}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch(close())} color="inherit">
            {t("actions.cancel")}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {t("actions.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const EquipmentDialog = () => {
  const open = useSelector(selectIsOpen);
  const initialData = useSelector(selectInitialFormData);
  if (!open || !initialData) {
    return null;
  }
  return <OpenEquipmentDialog initialData={initialData} />;
};

export default EquipmentDialog;
